import React, { useCallback } from 'react';
import { MMediaImage } from '../../molecules/MMediaImage';
import { AEyebrow } from '../../atoms/AEyebrow';
import { ASubheader } from '../../atoms/ASubheader';
import { AAccentLine, AAccentLineSize, AAccentLineAlignment } from '../../atoms/AAccentLine';
import { ABody } from '../../atoms/ABody';
import { MButton } from '../../molecules/MButton';
import { MTag, MTagType } from '../../molecules/MTag';
import * as Styled from './OCardStandard.styles';
import type { OCardStandardProps } from './OCardStandard.types';
import { OCardStandardContentAlignment, OCardStandardPadding } from './OCardStandard.types';
import { MButtonSize } from '../../molecules/MButton/MButton.types';
import type { MButtonProps, MButtonType } from '../../molecules/MButton/MButton.types';

const COMPONENT_NAME = 'o-web-ui-kit-OCardStandard';
const TEST_ID = 'web-ui-kit-OCardStandard';

export const OCardStandard: React.FC<OCardStandardProps> = ({
  componentId,
  contentAlignment = OCardStandardContentAlignment.Left,
  padding = OCardStandardPadding.ContentOnly,
  mMediaImage,
  aEyebrow,
  aSubheader,
  aAccentLine,
  aBody,
  mButton,
  mButton2,
  mTag,
  ctaLink,
  className,
}) => {
  const hasPrimaryButton = isNonEmpty(mButton?.buttonText);
  const hasSecondaryButton = isNonEmpty(mButton2?.buttonText);
  const hasMediaButtonIcon = !!mMediaImage?.mButtonIcon?.aIcon?.icon;
  const isClickable = !!ctaLink && !hasPrimaryButton && !hasSecondaryButton && !hasMediaButtonIcon;

  const handleCardClick = useCallback(() => {
    if (isClickable && ctaLink) {
      window.location.href = ctaLink;
    }
  }, [isClickable, ctaLink]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isClickable && ctaLink && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        window.location.href = ctaLink;
      }
    },
    [isClickable, ctaLink]
  );

  if (!aSubheader || !isNonEmpty(aSubheader.text)) {
    return null;
  }

  const showButtons = hasPrimaryButton;
  const showSecondaryButton = hasPrimaryButton && hasSecondaryButton;

  const showEyebrow = isNonEmpty(aEyebrow?.eyebrowText);
  const showBody = isNonEmpty(aBody?.bodyText);

  // Accent Line and Tag are mutually exclusive; Tag takes priority
  const hasTagLabel = isNonEmpty(mTag?.aLabel?.text);
  const showAccentLine = !!aAccentLine && !hasTagLabel;
  const showTag = hasTagLabel;

  return (
    <Styled.Wrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      data-component-id={componentId}
      className={className}
      $contentAlignment={contentAlignment}
      $padding={padding}
      $isClickable={isClickable}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      role={isClickable ? 'link' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? aSubheader.text : undefined}
    >
      {mMediaImage && (
        <Styled.MediaWrapper $padding={padding} data-testid={`${TEST_ID}-media`}>
          <MMediaImage
            aImage={mMediaImage.aImage}
            mButtonIcon={mMediaImage.mButtonIcon}
            aScrimGradient={mMediaImage.aScrimGradient}
            mTag={mMediaImage.mTag}
          />
        </Styled.MediaWrapper>
      )}

      <Styled.ContentWrapper $contentAlignment={contentAlignment} $padding={padding}>
        {showEyebrow && aEyebrow && (
          <Styled.EyebrowWrapper>
            <AEyebrow eyebrowText={aEyebrow.eyebrowText} />
          </Styled.EyebrowWrapper>
        )}

        <Styled.SubheaderWrapper>
          <ASubheader text={aSubheader.text} size={aSubheader.size} element={aSubheader.element} />
        </Styled.SubheaderWrapper>

        {showAccentLine && aAccentLine && (
          <Styled.AccentLineWrapper>
            <AAccentLine
              size={AAccentLineSize.OnePx}
              alignment={
                contentAlignment === OCardStandardContentAlignment.Centered
                  ? AAccentLineAlignment.Centered
                  : AAccentLineAlignment.Left
              }
            />
          </Styled.AccentLineWrapper>
        )}

        {showTag && mTag && (
          <Styled.TagWrapper>
            <MTag type={MTagType.Alternate} aLabel={mTag.aLabel} aIcon={mTag.aIcon} className="m-badge-inline" />
          </Styled.TagWrapper>
        )}

        {showBody && aBody && (
          <Styled.BodyWrapper>
            <ABody bodyText={aBody.bodyText} size={aBody.size} color={aBody.color} />
          </Styled.BodyWrapper>
        )}

        {showButtons && mButton && (
          <Styled.ButtonsWrapper $contentAlignment={contentAlignment}>
            <Styled.ButtonWrapper>
              <MButton {...mapMButtonProps(mButton)} />
            </Styled.ButtonWrapper>

            {showSecondaryButton && mButton2 && (
              <Styled.ButtonWrapper>
                <MButton {...mapMButtonProps(mButton2)} />
              </Styled.ButtonWrapper>
            )}
          </Styled.ButtonsWrapper>
        )}
      </Styled.ContentWrapper>
    </Styled.Wrapper>
  );
};

type AemMButtonShape = MButtonProps & {
  type?: string;
};

function isNonEmpty(value: string | undefined | null): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function mapMButtonProps(mButton: AemMButtonShape | undefined): MButtonProps {
  if (!mButton) return {};
  const { type, buttonType, size, ...rest } = mButton;
  return {
    ...rest,
    buttonType: (buttonType ?? (type as MButtonType | undefined)) as MButtonType | undefined,
    size: (size as MButtonSize | undefined) ?? MButtonSize.S,
  };
}

export * from './OCardStandard.types';
